import { RequireAdmin } from './guards/RequireAdmin';
import { RequireAuth } from './guards/RequireAuth';
import { RequireAuthzPermission } from './guards/RequireAuthzPermission';
import { RequireDiscoveryKey } from './guards/RequireDiscoveryKey';
import { RequireEnvCondition } from './guards/RequireEnvCondition';
import { RequirePermission } from './guards/RequirePermission';
import { RequireWorkspaces } from './guards/RequireWorkspaces';
import type { RouteDefinition } from './routeDefinitions';

/**
 * Wraps route content with appropriate guards based on route definition
 */
export const applyRouteGuards = (route: RouteDefinition, props: unknown) => {
  const content = route.render(props);

  const withEnvCondition = route.requiresEnvCondition ? (
    <RequireEnvCondition check={route.requiresEnvCondition}>{content}</RequireEnvCondition>
  ) : (
    content
  );

  const withDiscoveryKey = route.requiresDiscoveryKey ? (
    <RequireDiscoveryKey discoveryKey={route.requiresDiscoveryKey}>{withEnvCondition}</RequireDiscoveryKey>
  ) : (
    withEnvCondition
  );

  const withWorkspaces = route.requiresWorkspaces ? (
    <RequireWorkspaces>{withDiscoveryKey}</RequireWorkspaces>
  ) : (
    withDiscoveryKey
  );

  const withPermissionCallback = route.requiresPermission ? (
    <RequirePermission check={route.requiresPermission}>{withWorkspaces}</RequirePermission>
  ) : (
    withWorkspaces
  );

  const needsAuthzCheck = route.requiresAuthzPermissionOR || route.requiresAuthzPermissionAND;

  const withAuthzPermission = needsAuthzCheck ? (
    <RequireAuthzPermission
      permissionOR={route.requiresAuthzPermissionOR}
      permissionAND={route.requiresAuthzPermissionAND}
    >
      {withPermissionCallback}
    </RequireAuthzPermission>
  ) : (
    withPermissionCallback
  );

  const withAdmin = route.requiresAdmin ? <RequireAdmin>{withAuthzPermission}</RequireAdmin> : withAuthzPermission;

  const needsAuth = route.requiresAuth || route.requiresAdmin || needsAuthzCheck || route.requiresPermission;
  return needsAuth ? <RequireAuth>{withAdmin}</RequireAuth> : withAdmin;
};
